/**
 * ScanPage — Immersive full-browser scan progress experience.
 * All UI strings are localised via i18next 'scan' and 'scoring' namespaces.
 */
import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowRight, AlertCircle, Calendar } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { scanApi } from '../api/client'
import { useScanStore } from '../stores/scanStore'
import { StageChecklist } from '../components/scan/StageChecklist'
import { MapScene } from '../components/scan/MapScene'
import { ReviewsScene } from '../components/scan/ReviewsScene'
import { PhotoStackScene } from '../components/scan/PhotoStackScene'
import { WebsiteAuditScene } from '../components/scan/WebsiteAuditScene'
import type {
  ProgressEvent,
  ScanStageKey,
  ReviewsSceneData,
  PhotosSceneData,
  WebsiteAuditSceneData,
  ScoreRevealSceneData,
  CategoryKey,
} from '../types'

import { APP_NAME, BRAND, CONSULTATION_URL } from '../config'
import { useI18nNavigate } from '../hooks/useI18nNavigate'
import { LanguageSwitcher } from '../components/LanguageSwitcher'
import logoHeader from '../assets/landing/logo-header.svg'

// ── Stage grouping ────────────────────────────────────────────────────────────

const MAP_STAGES: ScanStageKey[] = [
  'resolve_target_business',
  'discover_competitors',
  'check_rankings',
]

// ── Score reveal scene ────────────────────────────────────────────────────────

/** Maps a category key to its i18n label lookup, keeping the display order stable. */
function useCategoryLabels(): Record<CategoryKey, string> {
  const { t } = useTranslation('scan')
  return {
    search_results:   t('scene.scoreReveal.search'),
    guest_experience: t('scene.scoreReveal.experience'),
    local_listings:   t('scene.scoreReveal.listings'),
  }
}

/**
 * Shows the REAL per-business score breakdown once the backend has calculated
 * it (via the `score_reveal` scene payload). While the calculation is still in
 * flight (data is null), a neutral "calculating" indicator is shown instead of
 * fabricated numbers — every business gets its own true results.
 */
function ScoreRevealScene({ data }: { data: ScoreRevealSceneData | null }) {
  const { t } = useTranslation('scan')
  const categoryLabels = useCategoryLabels()

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-6">
        <motion.p
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-sm font-medium text-gray-500 uppercase tracking-wider"
        >
          {t('scene.scoreReveal.label')}
        </motion.p>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1.1, ease: 'linear' }}
          className="w-10 h-10 rounded-full border-4 border-gray-200"
          style={{ borderTopColor: BRAND.red }}
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center h-full gap-8">
      <motion.p
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-sm font-medium text-gray-500 uppercase tracking-wider"
      >
        {t('scene.scoreReveal.label')}
      </motion.p>
      <div className="flex items-end gap-10">
        {data.categories.map((cat, i) => (
          <motion.div
            key={cat.category}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.35 }}
            className="flex flex-col items-center gap-2"
          >
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: i * 0.35 + 0.2, type: 'spring', stiffness: 200 }}
              className="text-5xl font-black"
              style={{ color: BRAND.red }}
            >
              {cat.score}
            </motion.div>
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              {categoryLabels[cat.category] ?? cat.category}
            </div>
          </motion.div>
        ))}
      </div>
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        className="h-1 w-40 rounded-full"
        style={{ background: `linear-gradient(90deg, ${BRAND.navy}, ${BRAND.red})` }}
      />
    </div>
  )
}


// ── Terminal overlays ─────────────────────────────────────────────────────────


function FailedOverlay({ scanId }: { scanId?: string }) {
  const navigate = useI18nNavigate()
  const { t } = useTranslation('scan')
  return (
    <motion.div
      key="failed"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="absolute inset-0 flex items-center justify-center bg-[#fafaf8]"
    >
      <div className="bg-white rounded-2xl border border-red-100 shadow-sm p-10 text-center max-w-md mx-6">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">{t('failed.title')}</h3>
        <p className="text-sm text-gray-500 mb-6">{t('failed.desc')}</p>
        <button
          onClick={() => scanId && navigate(`/report/${scanId}`)}
          className="text-sm hover:underline font-medium"
          style={{ color: BRAND.red }}
        >
          {t('failed.viewPartial')}
        </button>
      </div>
    </motion.div>
  )
}

function CompletedOverlay({ scanId }: { scanId?: string }) {
  const navigate = useI18nNavigate()
  const { t } = useTranslation('scan')
  return (
    <motion.div
      key="completed"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="absolute inset-0 flex items-center justify-center bg-[#fafaf8]"
    >
      <div className="bg-white rounded-2xl border border-green-100 shadow-sm p-10 text-center max-w-md mx-6">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 260, damping: 18 }}
          className="text-6xl mb-4 select-none"
        >
          ✅
        </motion.div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">{t('completed.title')}</h3>
        <p className="text-sm text-gray-500 mb-6">{t('completed.desc')}</p>
        <button
          onClick={() => scanId && navigate(`/report/${scanId}`)}
          className="inline-flex items-center gap-2 text-white text-sm font-semibold px-6 py-2.5 rounded-xl"
          style={{ backgroundColor: BRAND.red }}
          onMouseOver={e => (e.currentTarget.style.backgroundColor = BRAND.redHover)}
          onMouseOut={e => (e.currentTarget.style.backgroundColor = BRAND.red)}
        >
          {t('completed.viewReport')} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  )
}

// ── Scene content ─────────────────────────────────────────────────────────────
// The scene "reel" is the SINGLE presenter of the data scenes. It always shows
// exactly 4 fixed, ordered slots — reviews → photos → audit → scores — driven
// by a timer (see the reel effect in ScanPage), never by backend stage timing.
// This guarantees every scene is displayed exactly once, with real dwell time,
// regardless of how fast or slow any particular backend stage happens to run.
// If a slot's data hasn't arrived yet when its turn comes up, the scene's own
// "loading" state (data=null) is shown instead of skipping it.

const SCENE_REEL_LENGTH = 4

interface SceneContentProps {
  reviewsScene: ReviewsSceneData | null
  photosScene: PhotosSceneData | null
  websiteAuditScene: WebsiteAuditSceneData | null
  scoreRevealScene: ScoreRevealSceneData | null
  /** 0-based index into the fixed 4-slot reel (reviews, photos, audit, scores). */
  sceneIndex: number
}

function resolveSceneContent(
  reviewsScene: ReviewsSceneData | null,
  photosScene: PhotosSceneData | null,
  websiteAuditScene: WebsiteAuditSceneData | null,
  scoreRevealScene: ScoreRevealSceneData | null,
  sceneIndex: number,
): { sceneKey: string; node: React.ReactNode } {
  const pool: { sceneKey: string; node: React.ReactNode }[] = [
    { sceneKey: reviewsScene      ? 'reviews-data' : 'reviews-loading', node: <ReviewsScene data={reviewsScene} /> },
    { sceneKey: photosScene       ? 'photos-data'  : 'photos-loading',  node: <PhotoStackScene data={photosScene} /> },
    { sceneKey: websiteAuditScene ? 'audit-data'   : 'audit-loading',   node: <WebsiteAuditScene data={websiteAuditScene} /> },
    { sceneKey: scoreRevealScene  ? 'scores-data'  : 'scores-loading',  node: <ScoreRevealScene data={scoreRevealScene} /> },
  ]
  const clampedIndex = Math.min(Math.max(sceneIndex, 0), pool.length - 1)
  return pool[clampedIndex]
}

function SceneContent({ reviewsScene, photosScene, websiteAuditScene, scoreRevealScene, sceneIndex }: SceneContentProps) {
  const { sceneKey, node } = resolveSceneContent(reviewsScene, photosScene, websiteAuditScene, scoreRevealScene, sceneIndex)

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 min-h-0 px-8 pb-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={sceneKey}
            className="h-full overflow-y-auto"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
          >
            {node}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}


// ── Main page ─────────────────────────────────────────────────────────────────

export function ScanPage() {
  const { scanId } = useParams<{ scanId: string }>()
  const navigate = useI18nNavigate()
  const { t } = useTranslation('scan')
  const { t: tCommon } = useTranslation('common')
  const cleanupRef = useRef<(() => void) | null>(null)

  const {
    stages,
    overallPercent,
    isCompleted,
    isFailed,
    businessName,
    businessLat,
    businessLng,
    locateScene,
    competitorMapScene,
    reviewsScene,
    photosScene,
    websiteAuditScene,
    scoreRevealScene,
    mapAnimationDone,
    applyEvent,
    markCompleted,
    setMapAnimationDone,
  } = useScanStore()


  useEffect(() => {
    if (!scanId) return
    const cleanup = scanApi.streamProgress(
      scanId,
      (ev: ProgressEvent) => {
        applyEvent(ev)
        if (ev.stage_key === 'finalize_report' && ev.status === 'completed') {
          markCompleted()
        }
      },
      () => markCompleted(),
    )
    cleanupRef.current = cleanup
    return () => cleanup()
  }, [scanId, applyEvent, markCompleted])

  const activeStage = Object.values(stages).find(s => s.status === 'running')
  const activeKey = activeStage?.key
  const activeMessage = activeStage?.message ?? t('message.analyzing')

  const lat = businessLat ?? locateScene?.lat ?? null
  const lng = businessLng ?? locateScene?.lng ?? null

  const isMapStage = activeKey != null && MAP_STAGES.includes(activeKey)
  const mapStagesCompletedOrSkipped =
    ['completed', 'skipped', 'failed'].includes(stages['resolve_target_business']?.status ?? '') &&
    ['completed', 'skipped', 'failed'].includes(stages['discover_competitors']?.status ?? '')

  /**
   * Show the map while:
   *  a) a map stage is actively running, OR
   *  b) the map stages are done/skipped but the animation hasn't finished yet.
   *
   * The map ALWAYS takes precedence over the completion overlay so it is never
   * interrupted mid-animation even if the backend finishes first.
   */
  const showMap = isMapStage || (!mapAnimationDone && mapStagesCompletedOrSkipped)

  // ── Scene reel ─────────────────────────────────────────────────────────────
  // The reel becomes the SINGLE presenter of the data scenes — reviews →
  // photos → audit → scores — each held for a fixed dwell (4s), driven purely
  // by a timer rather than backend stage timing. This guarantees every scene
  // is shown exactly once, in order, regardless of how fast or slow the
  // backend actually completes each stage.
  //
  // IMPORTANT: the reel must only be considered "active" while the map scene
  // is NOT actually being rendered on screen (`!showMap`), not merely once the
  // map's own zoom/tour animation has finished. `showMap` stays true for as
  // long as `check_rankings` (part of MAP_STAGES) is still running, which can
  // outlast the map animation itself. Starting the reel timers as soon as
  // `mapAnimationDone` flips true — while the map is still being displayed
  // because `check_rankings` hasn't finished — silently burns through reel
  // slots (reviews/photos/audit) behind the still-visible map, which is why
  // those scenes were sometimes never seen. Gating on `!showMap` instead means
  // the timers can't start ticking until the reel is actually visible.
  const reelActive = !isFailed && !showMap

  const [sceneIndex, setSceneIndex] = useState(0)
  const [reelDone, setReelDone] = useState(false)

  useEffect(() => {
    if (!reelActive) return

    // Reset in case this effect is re-triggered (e.g. reelActive flickered
    // false→true again) so a stale sceneIndex/reelDone can never persist.
    setSceneIndex(0)
    setReelDone(false)

    const SCENE_DWELL = 4000 // 4 seconds per scene
    const lastIndex = SCENE_REEL_LENGTH - 1
    const timers: ReturnType<typeof setTimeout>[] = []

    for (let i = 1; i <= lastIndex; i++) {
      timers.push(setTimeout(() => setSceneIndex(i), i * SCENE_DWELL))
    }
    // Allow completion only once the LAST scene (scores) has had its own full
    // dwell — not the instant it appears.
    timers.push(setTimeout(() => setReelDone(true), (lastIndex + 1) * SCENE_DWELL))

    return () => timers.forEach(clearTimeout)
  }, [reelActive])


  /**
   * Show the completion overlay only after:
   *  a) the scan backend is done (isCompleted), AND
   *  b) the reel is active (map finished or never shown) AND has fully played
   *     through every scene at least once.
   */
  const showCompleted = isCompleted && reelActive && reelDone



  // Auto-redirect is tied to showCompleted (not isCompleted) so it waits for
  // the scene reel to finish playing every scene before navigating away.

  useEffect(() => {
    if (!showCompleted || !scanId) return
    const timer = setTimeout(() => navigate(`/report/${scanId}`), 1500)
    return () => clearTimeout(timer)
  }, [showCompleted, scanId, navigate])

  const shellHeader = 'max-w-[1070px] mx-auto w-full px-5 sm:px-6'

  return (
    <div className="h-screen flex flex-col bg-[#f2f2f6] overflow-y-auto lg:overflow-hidden">
      {/* Header */}
      <header className="bg-white shrink-0 z-50 shadow-[0px_4px_2px_rgba(0,0,0,0.13)]">
        <div className={`${shellHeader} min-h-[80px] md:min-h-[100px] py-3 md:py-4 flex items-center justify-between gap-4`}>
          <img src={logoHeader} alt={APP_NAME} className="h-[62px] w-auto" />

          <nav className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2 sm:gap-x-4">
            <LanguageSwitcher variant="compact" />
            <a
              href={CONSULTATION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1.5 h-10 w-10 md:h-[34px] md:w-auto md:px-4 rounded-[10px] text-xs md:text-sm font-medium text-white transition-colors whitespace-nowrap md:gap-1.5"
              style={{ backgroundColor: BRAND.red }}
              onMouseOver={(e) => { e.currentTarget.style.backgroundColor = BRAND.redHover }}
              onMouseOut={(e) => { e.currentTarget.style.backgroundColor = BRAND.red }}
            >
              <Calendar className="w-4 h-4 md:w-4 md:h-4" />
              <span className="hidden md:inline">{tCommon('nav.book')}</span>
            </a>
          </nav>
        </div>
      </header>

      {/* Body */}
      <div className="flex flex-col lg:flex-row flex-1 overflow-y-auto lg:overflow-hidden border-t border-[#f3f4f6]">
        {/* Left sidebar */}
        <aside className="order-2 lg:order-1 w-full lg:w-[280px] shrink-0 bg-white border-t lg:border-t-0 lg:border-e border-[#f3f4f6] flex flex-col lg:overflow-hidden">
          <div className="p-5 lg:flex-1 lg:overflow-y-auto lg:min-h-0">
            <h2 className="text-xs font-bold text-[#99a1af] uppercase tracking-[1.2px] mb-5">
              {t('progress.title')}
            </h2>
            <StageChecklist stages={stages} />
          </div>

          <div className="hidden lg:block px-5 pb-5 pt-4 shrink-0">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-1.5 bg-[#f3f4f6] rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: `linear-gradient(90deg, ${BRAND.navy}, ${BRAND.red})` }}
                  initial={{ width: 0 }}
                  animate={{ width: `${overallPercent}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                />
              </div>
              <span className="text-xs font-bold text-[#364153] tabular-nums shrink-0 min-w-[2rem] text-right">
                {overallPercent}%
              </span>
            </div>
          </div>
        </aside>

        {/* Right: animated scene area — loader/map/scenes unchanged */}
        <main className="order-1 lg:order-2 flex-1 overflow-hidden relative bg-[#f2f2f6] flex flex-col min-h-[480px] lg:min-h-0">
          {!isFailed && !showCompleted && businessName && (
            <p className="text-sm font-medium text-[#6a7282] px-8 pt-6 shrink-0 leading-5">
              {t('progress.analyzing', { businessName })}
            </p>
          )}

          <div className="lg:hidden px-6 sm:px-8 py-4 shrink-0">
            <div className="flex items-center gap-3">
              <div className="flex-1 h-1.5 bg-[#f3f4f6] rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: `linear-gradient(90deg, ${BRAND.navy}, ${BRAND.red})` }}
                  initial={{ width: 0 }}
                  animate={{ width: `${overallPercent}%` }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                />
              </div>
              <span className="text-xs font-bold text-[#364153] tabular-nums shrink-0 min-w-[2rem] text-right">
                {overallPercent}%
              </span>
            </div>
          </div>

          <div className="flex-1 relative min-h-0">
          <AnimatePresence mode="wait">
            {isFailed ? (
              // Always show the failure overlay immediately.
              <FailedOverlay key="failed" scanId={scanId} />
            ) : showMap ? (
              // Map takes priority over completion — never interrupt it mid-animation.
              <motion.div
                key="map-scene"
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: 0.4 } }}
              >
                <MapScene
                  businessLat={lat}
                  businessLng={lng}
                  businessName={businessName}
                  businessLogoUrl={locateScene?.logo_url}
                  competitors={competitorMapScene?.competitors ?? null}
                  activeKey={activeKey}
                  message={activeMessage}
                  onAnimationComplete={setMapAnimationDone}
                />
              </motion.div>
            ) : showCompleted ? (
              // Show completion only after the reel has cycled through every scene.
              <CompletedOverlay key="completed" scanId={scanId} />
            ) : (
              // Content scenes: the reel (sceneIndex) is the sole presenter —
              // see the "Scene reel" effect above for how it advances.
              <motion.div
                key="content-scene"
                className="absolute inset-0"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, transition: { duration: 0.3 } }}
              >
                <SceneContent
                  reviewsScene={reviewsScene}
                  photosScene={photosScene}
                  websiteAuditScene={websiteAuditScene}
                  scoreRevealScene={scoreRevealScene}
                  sceneIndex={sceneIndex}
                />
              </motion.div>
            )}

          </AnimatePresence>
          </div>
        </main>
      </div>
    </div>
  )
}
